import type { FieldError } from '../../core/contracts/field.binding';
import type { SurfaceDialogChrome } from '../../core/contracts/surface.dialog.chrome';

/** True while the form host should block Cancel/Save interactions. */
export function isFormHostBusy(state: { readonly initializing: boolean; readonly submitting: boolean; readonly saving: boolean }): boolean {

  return state.initializing || state.submitting || state.saving;
}

/** True when Save must not call submit (read-only, busy, invalid, or still validating). A read-only form has nothing to save. */
export function isFormHostSubmitDisabled(state: { readonly readonly: boolean; readonly busy: boolean; readonly invalid: boolean; readonly validating: boolean }): boolean {

  return state.readonly || state.busy || state.invalid || state.validating;
}

/** Merges page surface errors with the last submit failure for host banners. */
export function collectFormHostErrors(surfaceErrors: readonly FieldError[], submitErrors: readonly FieldError[]): readonly FieldError[] {

  return [...surfaceErrors, ...submitErrors];
}

/**
 * What a Form-host keyboard chord should do. `submit` saves; `submit-create-more` saves and arms the
 * footer's "Create more" switch first, so the dialog stays for the next record — but only when the
 * schema opted into createMore; otherwise the chord degrades to a plain save rather than dying silently.
 */
export function formHostSubmitIntent(semanticId: string, canCreateMore: boolean): { createMore: boolean } | null {

  if (semanticId === 'submit') return { createMore: false };
  if (semanticId === 'submit-create-more') return { createMore: canCreateMore };
  return null;
}

/**
 * Title bar only while a delegating frame (entity create dialog) is active for this mount. Absent in
 * `openFormSurfaceDialog` (its own header) and in embedded patch Forms — the mount hands them `null`.
 */
export function formHostDrawsDialogHeader(chrome: SurfaceDialogChrome | null): boolean {

  return chrome?.$active() === true;
}
